import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Package, ArrowRight } from "lucide-react";

const TIERS = [
  { name: "Mini courses", price: "$10 – $29", desc: "Short, focused skills — perfect for newcomers and quick wins.", color: "#D92D20" },
  { name: "Standard courses", price: "$49 – $99", desc: "Deep dives into employment, business, healthcare and more.", color: "#064E3B" },
  { name: "Advanced courses", price: "$149 – $299", desc: "Comprehensive programs with multiple modules and a final test.", color: "#D97706" },
];

export default function Pricing() {
  const [bundles, setBundles] = useState([]);
  useEffect(() => {
    api.get("/api/bundles").then(({data}) => setBundles(data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-page">
      <div className="text-center">
        <div className="label-eyebrow">Pricing</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mt-2 tracking-tight">Affordable courses for everyone.</h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl mx-auto">Pay once per course. Lifetime access, certificates included. No subscriptions.</p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {TIERS.map((t) => (
          <div key={t.name} className="bg-white border border-[#E5E7EB] rounded-2xl p-7 card-lift" data-testid={`tier-${t.name}`}>
            <div className="w-2 h-8 rounded-full" style={{ background: t.color }} />
            <div className="font-display font-bold text-xl mt-3">{t.name}</div>
            <div className="font-display font-black text-4xl mt-2">{t.price}</div>
            <p className="text-sm text-[#4B5563] mt-3">{t.desc}</p>
            <ul className="mt-5 text-sm space-y-2">
              <li>✓ Lifetime access</li>
              <li>✓ Free preview lessons</li>
              <li>✓ Certificate of completion</li>
              <li>✓ AI Tutor inside course</li>
            </ul>
          </div>
        ))}
      </div>

      {bundles.length > 0 && (
        <section className="mt-20" data-testid="bundles-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><Package className="w-3 h-3 text-[#D92D20]" /> Bundles</div>
              <h2 className="font-display font-black text-3xl md:text-4xl mt-1">Save more with bundles.</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bundles.map((b) => (
              <Link to={`/bundles/${b.slug}`} key={b.id} data-testid={`bundle-card-${b.slug}`}
                className="card-lift group block bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden bg-[#F3F4F6] relative">
                  <img src={b.thumbnail_url} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 right-3 bg-[#064E3B] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                    Save ${b.savings_usd?.toFixed(0)}
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-display font-bold text-lg leading-tight">{b.name}</div>
                  <p className="text-sm text-[#4B5563] mt-2 line-clamp-2">{b.description}</p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7EB]">
                    <div>
                      <span className="font-display font-black text-2xl">${b.price_usd?.toFixed(0)}</span>
                      <span className="text-sm text-[#4B5563] line-through ml-2">${b.original_total?.toFixed(0)}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#D92D20] group-hover:underline flex items-center gap-1">
                      View bundle <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                  <div className="text-xs text-[#4B5563] mt-2">{b.courses.length} courses included</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 text-center">
        <Link to="/courses" data-testid="pricing-browse" className="btn-primary inline-block">Browse all courses</Link>
      </div>

      <div className="mt-16 bg-white border border-[#E5E7EB] rounded-2xl p-8 text-center">
        <div className="label-eyebrow">Custom packages</div>
        <h3 className="font-display font-bold text-2xl mt-1">Need a tailored bundle?</h3>
        <p className="text-sm text-[#4B5563] mt-2">Get in touch and we'll build a learning path with savings for groups, families and employers.</p>
        <Link to="/contact" className="btn-outline inline-block mt-4" data-testid="pricing-contact">Contact us</Link>
      </div>
    </div>
  );
}
